import { useEffect, useState, useCallback } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import GetParsedDate from "../../components/shared/GetParsedDate";

const BASE_URL = process.env.REACT_APP_API;
const PAGE_SIZE = 10;

const StatCard = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <p className="text-xs text-gray-400 uppercase tracking-widest font-medium mb-1">
      {label}
    </p>
    <p className="text-2xl font-mono font-medium text-gray-900">
      {value ?? "—"}
    </p>
  </div>
);

const NavBtn = ({ onClick, disabled, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="btn btn-primary space-button px-4 py-2 bg-blue-600 rounded-md text-sm"
  >
    {children}
  </button>
);

const SkeletonRow = () => (
  <tr className="border-b border-gray-100 last:border-0">
    <td className="px-5 py-3">
      <div className="h-3 w-5 bg-gray-100 rounded animate-pulse" />
    </td>
    <td className="px-5 py-3">
      <div className="h-3 w-28 bg-gray-100 rounded animate-pulse" />
    </td>
    <td className="px-5 py-3">
      <div className="h-3 w-36 bg-gray-100 rounded animate-pulse" />
    </td>
  </tr>
);

function GetLoginLog() {
  const [data, setData] = useState({
    results: [],
    count: 0,
    next: null,
    previous: null,
  });

  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  // UI state (typing only)
  const [filters, setFilters] = useState({
    username: "",
    status: "",
    fromDate: "",
    toDate: "",
    fromHour: "",
    toHour: "",
  });

  // Applied filters (trigger API)
  const [appliedFilters, setAppliedFilters] = useState({
    username: "",
    status: "",
    fromDate: "",
    toDate: "",
    fromHour: "",
    toHour: "",
  });

  const axiosPrivate = useAxiosPrivate("application/json");

  const fetchLogs = useCallback(
    async (signal) => {
      setIsLoading(true);

      try {
        if (
          filters.fromDate &&
          filters.toDate &&
          filters.toDate < filters.fromDate
        ) {
          alert("תאריך סיום חייב להיות גדול או שווה לתאריך התחלה");
          return;
        }

        const parseHour = (h) => {
          const [hours, minutes] = h.split(":").map(Number);
          return hours * 60 + minutes;
        };

        if (
          filters.fromHour &&
          filters.toHour &&
          parseHour(filters.toHour) < parseHour(filters.fromHour)
        ) {
          alert("שעת סיום חייבת להיות גדולה או שווה לשעת התחלה");
          return;
        }
        const params = new URLSearchParams();
        params.append("page", page);

        if (appliedFilters.username)
          params.append("username", appliedFilters.username);
        if (appliedFilters.status)
          params.append("status", appliedFilters.status);
        if (appliedFilters.fromDate)
          params.append("from_date", appliedFilters.fromDate);
        if (appliedFilters.toDate)
          params.append("to_date", appliedFilters.toDate);
        if (appliedFilters.fromHour)
          params.append("from_hour", appliedFilters.fromHour);
        if (appliedFilters.toHour)
          params.append("to_hour", appliedFilters.toHour);

        const response = await axiosPrivate.get(
          `${BASE_URL}accounts/getloginlog/?${params.toString()}`,
          { signal },
        );

        setData(response.data);
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.error("Fetch error:", err);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [page, appliedFilters, axiosPrivate],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchLogs(controller.signal);
    return () => controller.abort();
  }, [fetchLogs]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleDeleteOldFailedLogs = async () => {
    // 1. Ask the user for confirmation with translation
    const isConfirmed = window.confirm(
      "האם אתה בטוח שברצונך למחוק את כל לוגי הכניסה הכושלים הישנים מחודש ומעלה?"
    );

    if (!isConfirmed) return;

    setLoading(true);
    setMessage('');

    try {
      // 2. Call the backend using axiosPrivate with the DELETE method
      const response = await axiosPrivate.delete(
        `${BASE_URL}accounts/deletefailedloginlog/`
      );

      const resData = response.data;
      setMessage(`${resData.deleted_count} רשומות נמחקו בהצלחה.`);

      // 3. Automatically refresh the log list/table after successful deletion
      const controller = new AbortController();
      fetchLogs(controller.signal);

    } catch (error) {
      console.error('בעיה במחיקת הרשומות:', error);
      setMessage(
        error.response?.data?.message || 'מחיקת הרשומות נכשלה. נסה שוב.'
      );
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.count || 0) / PAGE_SIZE));

  return (
    <div className="max-w-full mx-auto py-10 px-6">
      {/* Header */}
      <div className="text-center mb-6">
        <h3 className="text-sm text-gray-400">מעקב כניסות למערכת</h3>
      </div>

      {/* Filters */}
      <div className="flex flex-nowrap gap-3 mb-4 items-center overflow-x-auto ">
        <input
          type="text"
          placeholder="שם משתמש"
          value={filters.username}
          onChange={(e) =>
            setFilters((f) => ({ ...f, username: e.target.value }))
          }
          className="border rounded px-1.5 py-0.5 text-[10px] h-6 min-w-[40px] w-16"
          style={{ maxWidth: "150px" }}
        />

        <select
          value={filters.status}
          onChange={(e) =>
            setFilters((f) => ({ ...f, status: e.target.value }))
          }
          className="border rounded-md px-3 py-2 text-sm"
          style={{ maxWidth: "130px" }}
        >
          <option value="">כל הסטטוסים</option>
          <option value="SUCCESS">SUCCESS</option>
          <option value="FAILED">FAILED</option>
        </select>

        <input
          type="date"
          value={filters.fromDate}
          onChange={(e) =>
            setFilters((f) => ({ ...f, fromDate: e.target.value }))
          }
          className="border rounded-md px-3 py-2 text-sm"
        />

        <input
          type="date"
          value={filters.toDate}
          onChange={(e) =>
            setFilters((f) => ({ ...f, toDate: e.target.value }))
          }
          className="border rounded-md px-3 py-2 text-sm"
        />

        <select
          value={filters.fromHour}
          onChange={(e) =>
            setFilters((f) => ({ ...f, fromHour: e.target.value }))
          }
          className="border rounded-md px-3 py-2 text-sm"
          style={{ maxWidth: "100px" }}
        >
          <option value="">משעה</option>
          {[...[...Array(24).keys()].map((h) => `${h}:00`), "23:59"].map(
            (val) => (
              <option key={val} value={val}>
                {val}
              </option>
            ),
          )}
        </select>

        <select
          value={filters.toHour}
          onChange={(e) =>
            setFilters((f) => ({ ...f, toHour: e.target.value }))
          }
          className="border rounded-md px-3 py-2 text-sm"
          style={{ maxWidth: "100px" }}
        >
          <option value="">עד שעה</option>
          {[...[...Array(24).keys()].map((h) => `${h}:00`), "23:59"].map(
            (val) => (
              <option key={val} value={val}>
                {val}
              </option>
            ),
          )}
        </select>

        <button
          onClick={() => {
            setPage(1);
            setAppliedFilters(filters);
          }}
          className="btn btn-primary space-button flex-shrink-0"
          style={{ marginTop: "-6px" }}
        >
          סנן
        </button>

        <button
          onClick={() => {
            const empty = {
              username: "",
              status: "",
              fromDate: "",
              toDate: "",
              fromHour: "",
              toHour: "",
            };
            setFilters(empty);
            setAppliedFilters(empty);
            setPage(1);
          }}
          className="btn btn-primary space-button flex-shrink-0"
          style={{ marginTop: "-6px" }}
        >
          נקה פילטרים
        </button>

        <button
          onClick={handleDeleteOldFailedLogs}
          disabled={loading}
          className="btn btn-danger space-button flex-shrink-0"
          style={{
            cursor: loading ? 'not-allowed' : 'pointer',
            marginTop: "-6px",
          }}
        >
          {loading ? 'מחיקה...' : 'מחיקת לוגי כניסה כושלים ישנים יותר מחודש'}
        </button>

        {message && <p style={{ marginTop: '10px', fontSize: '14px' }}>{message}</p>}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-5 py-3 text-left text-xs" style={{ maxWidth: "80px" }}>#</th>
              <th className="px-5 py-3 text-left text-xs" style={{ maxWidth: "150px" }}>שם משתמש</th>
              <th style={{ maxWidth: "250px" }}> זמן </th>
              <th className="px-5 py-3 text-left text-xs" style={{ maxWidth: "100px" }}>סטטוס</th>
              <th className="px-5 py-3 text-left text-xs" style={{ maxWidth: "150px" }}>IP</th>
              <th className="px-5 py-3 text-left text-xs">User Agent</th>
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : data.results.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-10 text-gray-400">
                  אין נתונים
                </td>
              </tr>
            ) : (
              data.results.map((log, index) => (
                <tr key={log.id} className="border-b hover:bg-gray-50">
                  <td className="px-5 py-3 text-xs text-gray-400" style={{ maxWidth: "80px" }}>
                    {(page - 1) * PAGE_SIZE + index + 1}
                  </td>
                  <td className="px-5 py-3" style={{ maxWidth: "150px" }}>
                    {log.username}
                  </td>
                  <td style={{ maxWidth: "250px" }}>
                    <GetParsedDate strDate={log.login_time} withTime />
                  </td>
                  <td className="px-5 py-3" style={{ maxWidth: "100px" }}>
                    {log.status}
                  </td>
                  <td className="px-5 py-3" style={{ maxWidth: "150px" }}>
                    {log.ip_address}
                  </td>
                  <td className="px-5 py-3 text-xs">{log.user_agent}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-6 flex justify-between items-center">
        <p className="text-sm text-gray-500">
          סה"כ רשומות : {isLoading ? "—" : data.count}
          {"  ---- "}
          עמוד <b>{page}</b> מתוך <b>{totalPages}</b>
        </p>

        <div className="flex gap-2">
          <NavBtn onClick={() => setPage(1)} disabled={page === 1}>
            ראשון
          </NavBtn>

          <NavBtn
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            הקודם
          </NavBtn>

          <NavBtn onClick={() => setPage((p) => p + 1)} disabled={!data.next}>
            הבא
          </NavBtn>

          <NavBtn
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
          >
            אחרון
          </NavBtn>
          <br />
          <br />
        </div>
      </div>
    </div>
  );
}

export default GetLoginLog;